// ─────────────────────────────────────────────────────────────────────────────
// Load classification — presentational layer.
//
// The classification KEY (recommended / matched / potential / duplicate /
// not_matched) is resolved upstream in `constants/load-status.ts`
// (`verdictForTier`, from the per-search base_tier). This
// file owns the *visual* mapping (JSX), so the constants/data layer stays free
// of icons and components:
//
//   • a colored lucide icon whose shape signals the meaning at a glance, and
//   • the row's left-border color (so the list is scannable by color top-down).
//
// Hovering the icon reveals the word (+ the AI's reason, when present).
// ─────────────────────────────────────────────────────────────────────────────

import { Ban, CircleCheck, CircleDashed, Copy, Star, type LucideIcon } from 'lucide-react'
import { Tooltip } from 'components/ds'
import { resolveLoadStatusLabel } from 'constants/load-status'
import { cn } from 'utils/cn'

interface ClassificationUi {
    /** lucide icon whose shape carries the meaning (star = top pick, etc.). */
    Icon: LucideIcon
    /** Icon stroke color (light + dark). */
    iconClass: string
    /** Row left-border color (light + dark) — applied to the <tr> in LoadRow. */
    borderClass: string
}

const CLASSIFICATION_UI: Record<string, ClassificationUi> = {
    recommended: { Icon: Star, iconClass: 'text-blue-500 dark:text-blue-400', borderClass: 'border-l-blue-500/70 dark:border-l-blue-400/70' },
    matched: { Icon: CircleCheck, iconClass: 'text-green-600 dark:text-green-400', borderClass: 'border-l-green-500/70 dark:border-l-green-400/70' },
    potential: { Icon: CircleDashed, iconClass: 'text-amber-500 dark:text-amber-400', borderClass: 'border-l-amber-500/70 dark:border-l-amber-400/70' },
    duplicate: { Icon: Copy, iconClass: 'text-yellow-500 dark:text-yellow-400', borderClass: 'border-l-yellow-500/70 dark:border-l-yellow-400/70' },
    not_matched: { Icon: Ban, iconClass: 'text-slate-400 dark:text-slate-500', borderClass: 'border-l-slate-400/70 dark:border-l-slate-500/70' },
}

/** Row left-border color for a classification key, or null when there's none
 *  (unscored / in-flight) so the row keeps its default border. */
export const getClassificationBorderClass = (classification: string | null | undefined): string | null =>
    (classification && CLASSIFICATION_UI[classification]?.borderClass) || null

interface LoadClassificationIconProps {
    /** Classification key from rowMeta, or null/undefined → renders nothing. */
    classification: string | null | undefined
    /** AI's status_reason, appended to the hover tooltip when present. */
    reason?: string | null
}

/** Far-left colored icon marker for a load's AI fit-classification. Renders
 *  nothing when the load has no classification (unscored / in-flight). */
export const LoadClassificationIcon = ({ classification, reason }: LoadClassificationIconProps) => {
    if (!classification) return null
    const ui = CLASSIFICATION_UI[classification]
    if (!ui) return null
    const label = resolveLoadStatusLabel(classification)
    const { Icon } = ui
    return (
        <Tooltip title={reason ? `${label} — ${reason}` : label} placement="right">
            <span className="inline-flex items-center justify-center" aria-label={label}>
                <Icon className={cn('h-3.5 w-3.5', ui.iconClass)} strokeWidth={2} aria-hidden />
            </span>
        </Tooltip>
    )
}
